//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { IRightsOption } from '@legacy-components/rights-management/request-rights-model';
import ClientId from '@models/ClientId';
import Cuid from '@models/Cuid';
import { IRightsPermissions } from '@models/IItemMetadata';
import ItemId from '@models/ItemId';
import PartialCuid from '@models/PartialCuid';
import CommunicationType from '@models/RightsCommunicationType';
import { SocialNetwork } from '@models/SocialNetwork';
import { IRightsRequest } from '@uia/rights-request/rights-request-model';

import { Locales } from 'app/constants/locales/locale';

export interface IGetTermsAndConditions {
  data: {
    termsAndConditions: ITermsAndConditions;
  };
}

export interface ITermsAndConditionsResponse {
  data: ITermsAndConditions;
}

export interface ITermsAndConditions {
  termsAndConditionsUrl: string;
  isCustom: boolean;
}

export interface MarkRightsApprovedRequest {
  cuids: PartialCuid[];
  // Backend expeects a number. Used as a Date otherwise
  expirationTime?: number;
}

export type MarkRightsApprovedResponse = Record<
  PartialCuid,
  IRightsPermissions
>;

export interface SkipRightsRequest {
  cuids: PartialCuid[];
}

export interface InitiateRightsRequestRequest {
  approvalPhrase?: string;
  communicationType?: RightsRequestCommunicationType;
  metadata?: RightsRequestPartialMetadata;
  requestCreationMethod?: RightsRequestCreationMethod;
  requestMessage?: string;
}

export interface RightsRequestPartialMetadata {
  requestMessageLanguage?: Locales;
  requestMessageSource?: RightsRequestMessageSource;
}

export interface InitiateRightsRequestResponse {
  itemId: ItemId;
  network: SocialNetwork;
  status: RIGHTS_STATUS;
}

export interface RightsConfigurationsRequest {
  networkItemId: PartialCuid | undefined;
}

export interface RightsAccount {
  accountId: string;
}

export type RightsConfigurationsResponse = IRightsOption[];

export interface SyndicatedContentStatusRequest {
  creatorClientId: ClientId;
  cuid: Cuid;
}

export interface SyndicatedContentStatusResponse {
  isSyndicated: boolean;
}

export type HistoricalUrlsResponse = TermsAndConditions[];

export interface TermsAndConditions {
  termsAndConditionsUrl: string;
  isCustom: boolean;
}

// TODO: PD-263920: The frontend CommunicationType appears to just be a tiny type that is casted as "DM"
export interface IMultiHandleRightsRequestApiResponse {
  approvalPhrase?: string;
  communicationType: CommunicationType;
  requestMessage?: string;
  status: RIGHTS_STATUS;
}

export interface IMultiHandleRightsGraphApiRequest {
  partialCuid: PartialCuid;
  params: IRightsRequest;
}

export interface IRightsCapabilitiesMultiHandleResponse {
  isRightsCapable: boolean;
}

export interface DetectLanguageResponse {
  languageCode: string;
}

export interface SetRightsExpiryDateRequest {
  cuid: Cuid;
  expirationTime: Date | undefined;
}

export enum RightsRequestCommunicationType {
  BV_CREATORS_CAMPAIGN = 'BV_CREATORS_CAMPAIGN',
  BV_SAMPLING_CAMPAIGN = 'BV_SAMPLING_CAMPAIGN',
  COMMENT = 'COMMENT',
  DM = 'DM',
}

export enum RightsRequestMessageSource {
  /** A message created by AI that was then edited by the user. */
  AiAssisted = 'AiAssisted',
  /** A message generated entirely by AI and used without changes. */
  AiGenerated = 'AiGenerated',
  ConfiguredMessage = 'ConfiguredMessage',
  Custom = 'Custom',
}

export enum RightsRequestCreationMethod {
  ManualCopyPasteFlow = 'ManualCopyPasteFlow',
  ManualApiFlow = 'ManualApiFlow',
  AutomatedRequest = 'AutomatedRequest',
  RequestFromThirdParty = 'RequestFromThirdParty',
}

export enum RIGHTS_STATUS {
  APPROVED = 'approved',
  BRAND_OWNED = 'brandowned',
  FAILED_TO_SEND = 'failedtosend',
  NONE = 'none',
  NOT_SUPPORTED = 'notsupported',
  PENDING = 'pending',
  INITIATED = 'initiated',
  QUEUED = 'queued',
  EXPIRED = 'expired',
  UNSENT = 'unsent',
  CANCELLED = 'cancelled',
}
